import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import OfficeProductForm, {
  OfficeProductFormValues,
  SelectOption,
} from "components/OfficeProductForm";
import { Media } from "types";
import { showError, showSuccess } from "toast/toast";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles, } from "components/Imageshelper";
import { useBrandCategorySelector } from "components/BrandCategorySelector";
import { getAuthCookie } from "../../utils/authCookies";
// ===== Types that match your API =====
type Office = {
  _id: string;
  officeName: string;
  officeType: string;
  officeAddress: Array<{
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  }>;
};

type Brand = { id?: string; name: string };
type Category = { id?: string; name: string };

type OfficeInventoryObj = {
  product_id: string;
  office: Office;
  product: any; // keep loose to avoid changing your existing shapes
};

// ===== Static options =====
const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const CONDITION_OPTIONS: SelectOption[] = [
  { value: "new", label: "New" },
  { value: "used", label: "Used" },
  { value: "damaged", label: "Damaged" },
];

export default function RealtorAddEditOfficeProduct() {
  const { officeId, productId } = useParams<{ officeId?: string; productId?: string }>();
  const isEdit = Boolean(productId);
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';

  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";

  // ✅ Compute the ID to use for brand-category selector
  // For office products: use officeId from route (edit mode) or userId (add mode)
  // Following AddAgentOfficeProduct pattern: when office is selected, use the appropriate ID
  const entityIdForBrandCategory = useMemo(() => {
    // In edit mode, use officeId from route
    if (isEdit && officeId) {
      return userId || "";
    }
    // In add mode, use userId (logged-in user) - office will be selected in form
    return userId || "";
  }, [isEdit, officeId, userId]);

  // ✅ Use the brand-category selector hook
  const {
    selectedBrand,
    setSelectedBrand,
    selectedCategories,
    setSelectedCategories,
    brandOptions: brandOptionsFromHook,
    filteredCategories: categoryOptionsFromHook,
    loadingBrands,
    loadingCategories,
  } = useBrandCategorySelector(entityIdForBrandCategory, token || "");

  // data sources
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeProduct, setOfficeProduct] = useState<OfficeInventoryObj | null>(null);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);


  useEffect(() => {
    (async () => {
      try {
        // Fetch offices based on login type
        let offRes;
        if (isTeammember && userId) {
          // For teammember: fetch assigned offices
          offRes = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}/assigned-offices`, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
        } else {
          // For realtor: fetch all offices
          offRes = await fetch(`${process.env.REACT_APP_API_URL}/offices`, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
        }

        const offJson = await offRes.json();
        setOffices(Array.isArray(offJson) ? offJson : []);
        // ✅ Brands and categories are now loaded via useBrandCategorySelector hook above
      } catch (e: any) {
        showError(e.message || "Failed to load data");
      }
    })();
  }, [token, isTeammember]);

  // ---------- Fetch product (edit) ----------
  useEffect(() => {
    if (!isEdit || !officeId || !productId) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error("Failed to fetch product");
        setOfficeProduct(data);
        setUploadedFileMeta(data?.product?.variants?.[0]?.images || []);
      } catch (e: any) {
        showError(e.message || "Failed to load product");
      }
    })();
  }, [isEdit, officeId, productId, token]);

  // ---------- Helpers ----------
  const officeOptions = useMemo<SelectOption[]>(
    () => (Array.isArray(offices) ? offices : []).map(o => ({ value: o._id, label: o.officeName })),
    [offices]
  );
  
  // ✅ Brand options from hook (with product's current brand included if not in list)
  const brandOptions = useMemo<SelectOption[]>(() => {
    const brandOptions = brandOptionsFromHook;
    const productBrandId = officeProduct?.product?.brand_id || officeProduct?.product?.brand || "";
    const productBrandName = officeProduct?.product?.brand || "";

    if (productBrandId && productBrandName) {
      const brandExists = brandOptions.some(opt => opt.value === productBrandId);
      if (!brandExists) {
        return [
          { value: productBrandId, label: productBrandName },
          ...brandOptions
        ];
      }
    }

    return brandOptions;
  }, [brandOptionsFromHook, officeProduct]);

  // ✅ Category options from hook (filtered by selected brand, with product's current category included if not in list)
  const categoryOptions = useMemo<SelectOption[]>(() => {
    // ✅ Only return categories if brand is selected
    const categoryOptions = selectedBrand ? categoryOptionsFromHook : [];
    const productCategoryId = officeProduct?.product?.category_id || officeProduct?.product?.category || "";
    const productCategoryName = officeProduct?.product?.category || "";

    if (productCategoryId && productCategoryName) {
      const categoryExists = categoryOptions.some(opt => opt.value === productCategoryId);
      if (!categoryExists) {
        return [
          { value: productCategoryId, label: productCategoryName },
          ...categoryOptions
        ];
      }
    }

    return categoryOptions;
  }, [categoryOptionsFromHook, selectedBrand, officeProduct]);

  const getOfficeById = (id: string) => (Array.isArray(offices) ? offices : []).find(o => o._id === id) || null;

  // upload previews and merge with existing uploaded meta
  async function uploadProductImagesToCloudDrive(): Promise<string[]> {
    const tok = (token || "").trim() || null;
    // use the files you already collected in previewFiles
    const files = (previewFiles || []).map(p => p.file);
    if (!files.length) return [];

    const ids = await uploadFilesAndGetIds(files, tok);
    setPreviewFiles([]);           // clear after successful upload
    return ids;                    // send back to onSubmit
  }
  // If you have a single product object in edit screen:
  const dataForMap = React.useMemo(() => (officeProduct ? officeProduct : []), [officeProduct]);
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  // ---------- Formik ----------
  const formik = useFormik<OfficeProductFormValues>({
    enableReinitialize: true,
    initialValues: {
      officeId: "",

      name: officeProduct?.product?.name || "",
      sku: officeProduct?.product?.sku || "",
      qty: officeProduct?.product?.variants?.[0]?.inventory?.[0]?.available ?? 0,
      threshold_qty: officeProduct?.product?.threshold_qty ?? 0,
      brand: (officeProduct?.product as any)?.brand_id || officeProduct?.product?.brand || "",
      category: (officeProduct?.product as any)?.category_id || officeProduct?.product?.category || "",
      item_condition: officeProduct?.product?.item_condition || "",
      length: officeProduct?.product?.package_dimensions?.length?.toString?.() || "",
      height: officeProduct?.product?.package_dimensions?.height?.toString?.() || "",
      width: officeProduct?.product?.package_dimensions?.width?.toString?.() || "",
      weight: officeProduct?.product?.package_dimensions?.weight?.toString?.() || "",
      description: officeProduct?.product?.description || "",
      statuss: officeProduct?.product?.status || "Active",
      images: null,
    },
    validationSchema: Yup.object({
      officeId: isEdit ? Yup.string() : Yup.string().required("Office is required"),
      name: Yup.string().min(2).required("Product name is required"),
      sku: Yup.string().required("SKU is required"),
      qty: Yup.number().typeError("Quantity must be a number").min(0).required("Quantity is required"),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitting(true);

        // Extract brand details from options
        const selectedBrandId = String(values.brand || "");
        const selectedBrandOption = brandOptions.find(
          (b) => b.value === selectedBrandId
        );
        const selectedBrandName = selectedBrandOption?.label || "";

        // Extract category details from options
        const selectedCategoryId = String(values.category || "");
        const selectedCategoryOption = categoryOptions.find(
          (c) => c.value === selectedCategoryId
        );
        const selectedCategoryName = selectedCategoryOption?.label || "";

        const newImageIds = await uploadProductImagesToCloudDrive();

        // 2) Convert *existing* (kept) images from state as-is
        const kept: Media[] = (uploadedFileMeta ?? []).map((m: any) => ({
          url: m?.url,
          alt_text: m?.alt_text || "",
          default: !!m?.default,
        }));

        // 3) Convert *new* uploads into Media[]
        const newMedia: Media[] = newImageIds.map((id, idx) => ({
          url: id,
          alt_text: (previewFiles?.[idx]?.file?.name as string) || "",
          default: false,
        }));

        // 4) Merge and ensure exactly one default (if none, make the first one default)
        let images: Media[] = [...kept, ...newMedia];
        if (!images.some((m) => m.default) && images.length > 0) {
          images = images.map((m, i) => ({ ...m, default: i === 0 }));
        }

        try {
          if (pendingDeleteIds.length > 0) {
            await hardDeletePresignedFiles(pendingDeleteIds, token || null);
            setPendingDeleteIds([]); // clear
          }
        } catch (delErr: any) {
          console.error("Image hard delete (batch) failed:", delErr);
          // Don't block success — just inform
          showError(delErr?.message || "Some removed images could not be deleted.");
        }

        if (!isEdit) {
          const selected = getOfficeById(values.officeId);
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/office/${values.officeId}/inventory`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                inventory_type: "office",
                office: selected && {
                  id: selected._id,
                  officeName: selected.officeName,
                  officeType: selected.officeType,
                  officeAddress: {
                    addressLine1: selected.officeAddress?.[0]?.addressLine1,
                    addressLine2: selected.officeAddress?.[0]?.addressLine2,
                    city: selected.officeAddress?.[0]?.city,
                    state: selected.officeAddress?.[0]?.state,
                    country: selected.officeAddress?.[0]?.country,
                    zip: selected.officeAddress?.[0]?.zip,
                  },
                },
                product: {
                  name: values.name,
                  description: values.description,
                  sku: values.sku,
                  qty: Number(values.qty),
                  brand: selectedBrandName,
                  brand_id: selectedBrandId,
                  category: selectedCategoryName,
                  category_id: selectedCategoryId || null,
                   images: images || [],
                  item_condition: values.item_condition,
                  purchase_price: 1,
                  retail_price: 1,
                  package_dimensions: {
                    length: Number(values.length || 0),
                    height: Number(values.height || 0),
                    width: Number(values.width || 0),
                    weight: Number(values.weight || 0),
                  },
                  variants: [
                    {
                      variant_name: values.name,
                      sku: values.sku,
                      attributes: null,
                      purchase_price: 1,
                      retail_price: 1,
                      images: images || [],
                      package_dimensions: {
                        length: Number(values.length || 0),
                        height: Number(values.height || 0),
                        width: Number(values.width || 0),
                        weight: Number(values.weight || 0),
                        weight_type: "lb",
                      },
                      inventory: [
                        {
                          on_hand: Number(values.qty),
                          available: Number(values.qty),
                          reserved: 0,
                        },
                      ],
                    },
                  ],
                  threshold_qty: Number(values.threshold_qty || 0),

                  status: values.statuss,
                  is_rotational: false,
                  linked_mode: "none",
                  locked_status: "unlocked",
                  linked_installer: null,
                },
              }),
            }
          );
          if (!res.ok) throw new Error("Failed to save product");
          showSuccess("Product added successfully!");
        } else {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                product: {
                  name: values.name,
                  description: values.description,
                  brand: selectedBrandName,
                  brand_id: selectedBrandId,
                  category: selectedCategoryName,
                  category_id: selectedCategoryId || null,
                  item_condition: values.item_condition,
                  purchase_price: 1,
                  retail_price: 1,
                  package_dimensions: {
                    length: Number(values.length || 0),
                    height: Number(values.height || 0),
                    width: Number(values.width || 0),
                    weight: Number(values.weight || 0),
                  },
                  threshold_qty: Number(values.threshold_qty || 0),
                  // images: uploadedImages || [],
                  status: values.statuss,
                  is_rotational: false,
                  variants: [
                    {
                      variant_name: values.name,
                      sku: values.sku,
                      attributes: null,
                      purchase_price: 1,
                      retail_price: 1,
                      images: images || [],
                      package_dimensions: {
                        length: Number(values.length || 0),
                        height: Number(values.height || 0),
                        width: Number(values.width || 0),
                        weight: Number(values.weight || 0),
                        weight_type: "lb",
                      },
                      inventory: [
                        {
                          on_hand: Number(values.qty),
                          available: Number(values.qty),
                          reserved: 0,
                        },
                      ],
                    },
                  ],
                  linked_mode: "none",
                  locked_status: "unlocked",
                  linked_installer: null,
                },
              }),
            }
          );
          if (!res.ok) throw new Error("Failed to update product");
          showSuccess("Product updated successfully!");
        }

        navigate(`${baseRoute}/officeInventory`);
      } catch (e: any) {
        showError(e.message || "Failed to save product");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ---------- Handlers passed to UI ----------
  const handleAddFiles = (files: File[]) => {
    if (!files?.length) return;
    const fresh = files.map(file => ({ file, url: URL.createObjectURL(file) }));
    setPreviewFiles(prev => [...prev, ...fresh]);
  };

  // Remove a preview by its index (revoke URL + pop from state)
  const handleRemovePreview = (index: number) => {
    setPreviewFiles(prev => {
      const next = [...prev];
      const it = next[index];
      if (it?.url) URL.revokeObjectURL(it.url);
      next.splice(index, 1);
      return next;
    });
  };

  // Remove an already uploaded image **by index** (queue id for hard delete and update UI)
  const handleRemoveUploaded = (index: number) => {
    const id = String(uploadedFileMeta?.[index]?.url || "");
    if (!id) return;
    // queue for delete on save
    setPendingDeleteIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    // update UI now
    setUploadedFileMeta(prev => prev.filter((_, i) => i !== index));
  };

  // ✅ Refs to prevent circular updates and track previous values
  const isSyncingFromFormik = useRef(false);
  const isSyncingFromHook = useRef(false);
  const prevProductBrandId = useRef<string>("");
  const prevProductCategoryId = useRef<string>("");
  const prevFormikBrand = useRef<string>("");
  const prevFormikCategory = useRef<string>("");
  const prevSelectedBrand = useRef<string>("");
  const prevSelectedCategory = useRef<string>("");

  // ✅ Sync product's brand/category to hook when product loads (Edit mode) - Only once
  useEffect(() => {
    const productBrandId = officeProduct?.product?.brand_id || officeProduct?.product?.brand || "";
    if (productBrandId && productBrandId !== prevProductBrandId.current && productBrandId !== selectedBrand) {
      prevProductBrandId.current = productBrandId;
      // console.log("🔄 [Product→Hook] Setting brand from product:", productBrandId);
      setSelectedBrand(productBrandId);
    }
  }, [officeProduct, selectedBrand]);

  useEffect(() => {
    const productCategoryId = officeProduct?.product?.category_id || officeProduct?.product?.category || "";
    if (productCategoryId && productCategoryId !== prevProductCategoryId.current) {
      const currentFirst = selectedCategories[0] || "";
      if (productCategoryId !== currentFirst) {
        prevProductCategoryId.current = productCategoryId;
        // console.log("🔄 [Product→Hook] Setting category from product:", productCategoryId);
        setSelectedCategories([productCategoryId]);
      }
    }
  }, [officeProduct, selectedCategories]);

  // ✅ Monitor formik brand changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const brandValue = formik.values.brand || "";
    if (brandValue && brandValue !== prevFormikBrand.current && brandValue !== selectedBrand) {
      prevFormikBrand.current = brandValue;
      // console.log("🔄 [Formik→Hook] Brand changed:", brandValue);
      isSyncingFromFormik.current = true;
      setSelectedBrand(brandValue);
      setSelectedCategories([]); // Clear categories when brand changes
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.brand, selectedBrand]);

  // ✅ Monitor formik category changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const categoryValue = formik.values.category || "";
    const currentFirst = selectedCategories[0] || "";
    if (categoryValue && categoryValue !== prevFormikCategory.current && categoryValue !== currentFirst) {
      prevFormikCategory.current = categoryValue;
      // console.log("🔄 [Formik→Hook] Category changed:", categoryValue);
      isSyncingFromFormik.current = true;
      setSelectedCategories([categoryValue]);
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.category, selectedCategories]);

  // ✅ Monitor hook brand changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    if (selectedBrand && selectedBrand !== prevSelectedBrand.current && formik.values.brand !== selectedBrand) {
      prevSelectedBrand.current = selectedBrand;
      // console.log("🔄 [Hook→Formik] Brand changed:", selectedBrand);
      isSyncingFromHook.current = true;
      formik.setFieldValue("brand", selectedBrand, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    }
  }, [selectedBrand, formik.values.brand]);

  // ✅ Monitor hook category changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    const firstCategory = selectedCategories[0] || "";
    if (firstCategory && firstCategory !== prevSelectedCategory.current && formik.values.category !== firstCategory) {
      prevSelectedCategory.current = firstCategory;
      // console.log("🔄 [Hook→Formik] Category changed:", firstCategory);
      isSyncingFromHook.current = true;
      formik.setFieldValue("category", firstCategory, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    } else if (!firstCategory && formik.values.category) {
      // Clear category if hook has no selection
      if (prevSelectedCategory.current !== "") {
        prevSelectedCategory.current = "";
        // console.log("🔄 [Hook→Formik] Clearing category");
        isSyncingFromHook.current = true;
        formik.setFieldValue("category", "", false);
        setTimeout(() => { isSyncingFromHook.current = false; }, 0);
      }
    }
  }, [selectedCategories, formik.values.category]);

  // ---------- Render ----------
  return (
    <OfficeProductForm
      isEdit={isEdit}
      formik={formik as any}
      officeOptions={officeOptions}
      brandOptions={brandOptions}
      categoryOptions={categoryOptions}
      statusOptions={STATUS_OPTIONS}
      conditionOptions={CONDITION_OPTIONS}
      previewFiles={previewFiles}
      uploadedFileMeta={uploadedFileMeta}
      onRemoveUploaded={handleRemoveUploaded}
      onAddFiles={handleAddFiles}
      onRemovePreview={handleRemovePreview}
      title={isEdit ? "Edit Product" : "Add Product"}
      onBack={() => navigate(-1)}
      onSubmit={() => formik.handleSubmit()}
      submitting={submitting}
      baseUrl={process.env.REACT_APP_BASE_URL}
      resolveSrc={(url) =>
        getImageSrc(url, imageUrlMap) ||
        (String(url).startsWith("http") ? String(url)
          : `${process.env.REACT_APP_BASE_URL}/${String(url || "").replace(
            /^https?:\/\/[^/]+\/(uploads\/.*)/, '$1')}`)
      }
    />
  );
}
